import { useSuspenseQuery } from '@tanstack/react-query';
import type { UseSuspenseQueryResult } from '@tanstack/react-query';
import type { EndpointDef } from '@ooh/contracts';
import type { z } from 'zod';
import { useApiClient } from '../plugs/api-client';
import type { ApiClient } from '../plugs/api-client';

export function apiQueryKey<TDef extends EndpointDef>(
  def: TDef,
  request?: z.input<TDef['requestSchema']>,
): readonly unknown[] {
  return request === undefined ? [def.method, def.path] : [def.method, def.path, request];
}

// Exposed for useSuspenseQueries, which loads independent requests in parallel instead of one after another.
export function apiQueryOptions<TDef extends EndpointDef>(
  api: ApiClient,
  def: TDef,
  request: z.input<TDef['requestSchema']>,
) {
  return {
    queryKey: apiQueryKey(def, request),
    queryFn: ({ signal }: { signal: AbortSignal }) => api.call(def, request, { signal }),
  };
}

interface ApiSuspenseQueryOptions<TData> {
  staleTime?: number;
  refetchInterval?: (data: TData | undefined) => number | false;
}

export function usePublicApiSuspenseQuery<TDef extends EndpointDef>(
  def: TDef,
  request: z.input<TDef['requestSchema']>,
): UseSuspenseQueryResult<z.output<TDef['responseSchema']>> {
  const api = useApiClient();
  return useSuspenseQuery({
    queryKey: apiQueryKey(def, request),
    queryFn: ({ signal }) => api.callPublic(def, request, { signal }),
  });
}

export function useApiSuspenseQuery<TDef extends EndpointDef>(
  def: TDef,
  request: z.input<TDef['requestSchema']>,
  options: ApiSuspenseQueryOptions<z.output<TDef['responseSchema']>> = {},
): UseSuspenseQueryResult<z.output<TDef['responseSchema']>> {
  const { staleTime, refetchInterval } = options;
  return useSuspenseQuery({
    ...apiQueryOptions(useApiClient(), def, request),
    staleTime,
    refetchInterval: refetchInterval ? (query) => refetchInterval(query.state.data) : undefined,
  });
}
